import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';

/**
 * Operator support staff only: sign in as a customer's user to see exactly
 * what they see. Every start and stop lands in both organisations' audit
 * logs; API tokens, billing and licence purchases stay off-limits meanwhile.
 */
export function ImpersonateScreen(): ReactNode {
  const { user, impersonate } = useAuth();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const users = useQuery(() => api.impersonationUsers(debounced), [debounced]);

  if (!user?.canImpersonate) {
    return <EmptyState>Impersonation is only available to operator support staff.</EmptyState>;
  }

  async function start(userId: string): Promise<void> {
    setBusyId(userId);
    try {
      await impersonate(userId);
    } catch (err) {
      toast.error(err);
      setBusyId(null);
    }
  }

  return (
    <Page title="Impersonate">
      <p className="muted small" style={{ marginBottom: 12 }}>
        Sign in as a customer's user to see their console exactly as they do. The customer's audit log
        records that you did, and the session hands back to you automatically after a while. API tokens,
        billing and licence purchases are unavailable while impersonating.
      </p>

      <input
        type="search"
        placeholder="Search by name, email or organisation"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 12, width: '100%', maxWidth: 420 }}
        autoFocus
      />

      {users.loading && !users.data ? <Loading what="users" /> : null}
      {users.error ? <ErrorNote error={users.error} /> : null}

      {users.data ? (
        users.data.length === 0 ? (
          <EmptyState>No matching users.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Organisation</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div>{u.name}</div>
                      <div className="muted small">{u.email}</div>
                    </td>
                    <td>
                      <div>{u.tenant?.name ?? '—'}</div>
                      <div className="muted small mono">{u.tenant?.slug}</div>
                    </td>
                    <td className="muted small">{u.roleLabel}</td>
                    <td>
                      <button
                        className="btn small"
                        disabled={busyId !== null}
                        onClick={() => void start(u.id)}
                      >
                        {busyId === u.id ? 'Switching…' : 'Impersonate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Page>
  );
}
